import React, { createContext } from "react";
import { initializeApp, getApp, getApps } from "firebase/app";
import {
  getDatabase,
  ref,
  query,
  orderByChild,
  equalTo,
} from "firebase/database";
// Usamos el bundle estándar y el polyfill local de persistencia para RN
import {
  initializeAuth,
  getAuth,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPhoneNumber,
  PhoneAuthProvider,
  RecaptchaVerifier,
  unlink,
  updatePhoneNumber,
  linkWithPhoneNumber,
  browserLocalPersistence,
  browserPopupRedirectResolver,
} from "firebase/auth";
import { getReactNativePersistence } from "./react-native-persistance";
import { getStorage, ref as stRef } from "firebase/storage";

const FirebaseContext = createContext(null);

let firebase = {
  app: null,
  database: null,
  auth: null,
  storage: null,
};

const createFullStructure = (app, db, auth, storage, config) => {
  return {
    app: app,
    config: config,
    database: db,
    auth: auth,
    storage: storage,
    authRef: getAuth,
    googleProvider: new GoogleAuthProvider(),
    appleProvider: new OAuthProvider("apple.com"),
    phoneProvider: auth ? new PhoneAuthProvider(auth) : null,
    RecaptchaVerifier: RecaptchaVerifier,
    signInWithPhoneNumber: signInWithPhoneNumber,
    updatePhoneNumber: updatePhoneNumber,
    unlink: unlink,
    googleCredential: (idToken, accessToken) =>
      GoogleAuthProvider.credential(idToken, accessToken),
    linkWithPhoneNumber: linkWithPhoneNumber,
    mobileAuthCredential: (verificationId, code) =>
      PhoneAuthProvider.credential(verificationId, code),
    usersRef: ref(db, "users"),
    bookingRef: ref(db, "bookings"),
    cancelreasonRef: ref(db, "cancel_reason"),
    settingsRef: ref(db, "settings"),
    smtpRef: ref(db, "smtpdata"),
    smsRef: ref(db, "smsConfig"),
    carTypesRef: ref(db, "cartypes"),
    carTypesEditRef: (id) => ref(db, "cartypes/" + id),
    carDocImage: (id) => stRef(storage, `cartypes/${id}`),
    promoRef: ref(db, "promos"),
    promoEditRef: (id) => ref(db, "promos/" + id),
    promoImageRef: (id) => stRef(storage, `promos/${id}`),
    notifyRef: ref(db, "notifications/"),
    notifyEditRef: (id) => ref(db, "notifications/" + id),
    addressRef: (uid, id) => ref(db, "savedAddresses/" + uid + "/" + id),
    addressEditRef: (uid) => ref(db, "savedAddresses/" + uid),
    singleUserRef: (uid) => ref(db, "users/" + uid),
    profileImageRef: (uid) => stRef(storage, `users/${uid}/profileImage`),
    verifyIdImageRef: (uid) => stRef(storage, `users/${uid}/verifyIdImage`),
    bookingImageRef: (bookingId, imageType) =>
      stRef(storage, `bookings/${bookingId}/${imageType}`),
    driversRef: query(
      ref(db, "users"),
      orderByChild("usertype"),
      equalTo("driver"),
    ),
    driverDocsRef: (uid) => stRef(storage, `users/${uid}/license`),
    driverDocsRefBack: (uid) => stRef(storage, `users/${uid}/licenseBack`),
    vehicleInspectionRef: (uid) =>
      stRef(storage, `users/${uid}/vehicleInspection`),
    singleBookingRef: (bookingKey) => ref(db, "bookings/" + bookingKey),
    requestedDriversRef: (bookingKey) =>
      ref(db, "bookings/" + bookingKey + "/requestedDrivers"),
    dispatchLogRef: (bookingId) =>
      ref(db, "bookings/" + bookingId + "/dispatchLog"),
    dispatchMetaRef: (bookingId) =>
      ref(db, "bookings/" + bookingId + "/dispatchMeta"),
    referralIdRef: (referralId) =>
      query(ref(db, "users"), orderByChild("referralId"), equalTo(referralId)),
    trackingRef: (bookingId) => ref(db, "tracking/" + bookingId),
    tasksRef: () =>
      query(ref(db, "bookings"), orderByChild("status"), equalTo("NEW")),
    singleTaskRef: (uid, bookingId) =>
      ref(db, "bookings/" + bookingId + "/requestedDrivers/" + uid),
    bookingListRef: (uid, role) =>
      role == "customer"
        ? query(ref(db, "bookings"), orderByChild("customer"), equalTo(uid))
        : role == "driver"
          ? query(ref(db, "bookings"), orderByChild("driver"), equalTo(uid))
          : role == "fleetadmin"
            ? query(
                ref(db, "bookings"),
                orderByChild("fleetadmin"),
                equalTo(uid),
              )
            : ref(db, "bookings"),
    chatRef: (bookingId) => ref(db, "chats/" + bookingId + "/messages"),
    chatReadRef: (bookingId, uid) =>
      ref(db, "chats/" + bookingId + "/readState/" + uid),
    withdrawRef: ref(db, "withdraws/"),
    languagesRef: ref(db, "languages"),
    languagesEditRef: (id) => ref(db, "languages/" + id),
    walletHistoryRef: (uid) => ref(db, "walletHistory/" + uid),
    userNotificationsRef: (uid) => ref(db, "userNotifications/" + uid),
    userRatingsRef: (uid) => ref(db, "userRatings/" + uid),
    carsRef: (uid, role) =>
      role == "driver"
        ? query(ref(db, "cars"), orderByChild("driver"), equalTo(uid))
        : role == "fleetadmin"
          ? query(ref(db, "cars"), orderByChild("fleetadmin"), equalTo(uid))
          : ref(db, "cars"),
    carAddRef: ref(db, "cars"),
    carEditRef: (id) => ref(db, "cars/" + id),
    carImage: (id) => stRef(storage, `cars/${id}`),
    allLocationsRef: ref(db, "locations"),
    userLocationRef: (uid) => ref(db, "locations/" + uid),
    sosRef: ref(db, "sos"),
    editSosRef: (id) => ref(db, "sos/" + id),
    complainRef: ref(db, "complain"),
    editComplainRef: (id) => ref(db, "complain/" + id),
    paymentSettingsRef: ref(db, "payment_settings"),
    usedreferralRef: ref(db, "usedreferral"),
  };
};

const FirebaseProvider = ({ config, children, AsyncStorage, token }) => {
  let app, auth, database, storage;

  // Validate AsyncStorage is provided for React Native
  if (typeof document === "undefined" && !AsyncStorage) {
    console.warn(
      "FirebaseProvider: AsyncStorage not provided. Auth state will not persist between sessions.",
    );
  }

  try {
    app = getApps().length ? getApp() : initializeApp(config);
  } catch (error) {
    console.log("Error initializing Firebase app: " + error);
  }

  // Initialize database/storage even if auth fails, to avoid crashes in consumers
  try {
    if (app) {
      database = getDatabase(app);
    }
  } catch (error) {
    console.log("Error initializing Firebase database: " + error);
  }
  try {
    if (app) {
      storage = getStorage(app);
    }
  } catch (error) {
    console.log("Error initializing Firebase storage: " + error);
  }

  // Initialize auth last, with platform-specific handling
  try {
    if (app) {
      if (typeof document !== "undefined") {
        // Web: prefer existing auth, else initialize with browser persistence
        try {
          auth = getAuth(app);
        } catch (e) {
          auth = initializeAuth(app, {
            persistence: browserLocalPersistence,
            popupRedirectResolver: browserPopupRedirectResolver,
          });
        }
      } else {
        // React Native: Always try to initialize with AsyncStorage persistence first
        let rnAsyncStorage = AsyncStorage;
        if (!rnAsyncStorage) {
          try {
            // eslint-disable-next-line global-require
            rnAsyncStorage =
              require("@react-native-async-storage/async-storage").default;
          } catch (_e) {
            rnAsyncStorage = null;
            if (__DEV__) {
              console.log(
                "Warning: @react-native-async-storage/async-storage not available. Auth state will not persist.",
              );
            }
          }
        }

        // Always try to initialize with AsyncStorage if available
        // This ensures persistence is configured from the start
        if (rnAsyncStorage) {
          try {
            auth = initializeAuth(app, {
              persistence: getReactNativePersistence(rnAsyncStorage),
            });
          } catch (initError) {
            // If initializeAuth fails with "already-initialized", use getAuth
            if (
              initError.code === "auth/already-initialized" ||
              initError.message?.includes("already-initialized")
            ) {
              try {
                auth = getAuth(app);
                // Auth was already initialized, it should maintain its persistence settings
              } catch (getAuthError) {
                console.error(
                  "Error getting Firebase auth after already-initialized: " +
                    getAuthError,
                );
              }
            } else {
              console.error("Error initializing Firebase auth: " + initError);
              // Final fallback: try getAuth
              try {
                auth = getAuth(app);
              } catch (getAuthError) {
                console.error(
                  "Error getting Firebase auth as fallback: " + getAuthError,
                );
              }
            }
          }
        } else {
          // AsyncStorage not available - this will trigger the warning
          // But we still need to initialize auth
          if (__DEV__) {
            console.log(
              "Warning: Initializing Firebase Auth without AsyncStorage - auth state will not persist",
            );
          }
          try {
            auth = initializeAuth(app, {});
          } catch (initError) {
            if (
              initError.code === "auth/already-initialized" ||
              initError.message?.includes("already-initialized")
            ) {
              try {
                auth = getAuth(app);
              } catch (getAuthError) {
                console.error("Error getting Firebase auth: " + getAuthError);
              }
            } else {
              console.error("Error initializing Firebase auth: " + initError);
            }
          }
        }
      }
    }
  } catch (error) {
    // Final fallback: try getAuth if all else fails
    if (app && !auth) {
      try {
        auth = getAuth(app);
      } catch (e) {
        console.error("Error initializing Firebase auth: " + error);
      }
    } else {
      console.error("Error initializing Firebase auth: " + error);
    }
  }

  // Build context with whatever initialized (auth may be null temporarily)
  if (app && database && storage && auth) {
    firebase = createFullStructure(app, database, auth, storage, config);
  } else if (app && database && storage) {
    // Provide partial context to avoid crashes in DB usage
    firebase = createFullStructure(app, database, auth, storage, config);
  } else {
    console.error("Firebase core services not fully initialized.");
  }

  return (
    <FirebaseContext.Provider value={firebase}>
      {children}
    </FirebaseContext.Provider>
  );
};

export { firebase, FirebaseProvider, FirebaseContext };
